import type { Rect } from '../../doc/geometry';

/**
 * Raster brushes for sketch sheets. A stroke is a chain of stamps along the smoothed pointer
 * path. Paint brushes draw onto a scratch canvas that is composited onto the layer once, at
 * `opacity`, when the stroke ends, so a translucent stroke never builds up where it crosses
 * itself. The eraser works straight on the layer with destination-out, its per-stamp alpha
 * corrected for the overlap so the whole stroke erases `opacity` of what was there.
 *
 * What a stamp is depends on the brush: a round dab (fineliner, brush, eraser), a dab seen
 * through the paper's tooth (pencil), whole pixels with no anti-aliasing (pixel brush), the dots
 * of a halftone screen (halftone) or a puff of specks (spray). The tooth and the screen are
 * anchored to the paper, not to the stroke, so overlapping strokes agree with each other.
 */

export type BrushKind = 'pencil' | 'fineliner' | 'brush' | 'pixel' | 'halftone' | 'spray' | 'eraser' | 'fill' | 'picker' | 'lasso' | 'move';

/** Brushes that leave a stroke along the pointer path (the others act on a press or on the selection). */
export const STROKE_BRUSHES: BrushKind[] = ['pencil', 'fineliner', 'brush', 'pixel', 'halftone', 'spray', 'eraser'];

export const isStrokeBrush = (kind: BrushKind): boolean => STROKE_BRUSHES.includes(kind);

export interface BrushSettings {
  /** Diameter, sheet pixels. */
  size: number;
  /** 0..1, the whole stroke's opacity. */
  opacity: number;
  /** 0..1: 1 is a hard edge, lower fades from the centre. */
  hardness: number;
  /** 0..1, per-stamp alpha (brush, spray); the halftone's tone (how large its dots grow). */
  flow: number;
  pressureSize: boolean;
  pressureOpacity: boolean;
  /** Pencil: how much of the paper's tooth shows through the line, 0..1. */
  grain: number;
  /** Spray: how many specks a puff throws, 0..1. */
  density: number;
  /** Halftone: distance between the dots of the screen, sheet pixels. */
  pitch: number;
  /** Eraser and lasso: whole pixels only, no anti-aliased edge (the pixel brush always is). */
  aliased: boolean;
}

export interface BrushParams extends BrushSettings {
  kind: BrushKind;
  /** CSS hex colour. */
  color: string;
}

export const BRUSH_LABEL: Record<BrushKind, string> = {
  pencil: 'Pencil',
  fineliner: 'Fineliner',
  brush: 'Brush',
  pixel: 'Pixel brush',
  halftone: 'Halftone',
  spray: 'Spray',
  eraser: 'Eraser',
  fill: 'Fill',
  picker: 'Eyedropper',
  lasso: 'Lasso',
  move: 'Move',
};

export const BRUSH_SIZE_RANGE: Record<BrushKind, [number, number]> = {
  pencil: [1, 64],
  fineliner: [1, 64],
  brush: [2, 400],
  pixel: [1, 64],
  halftone: [4, 400],
  spray: [4, 400],
  eraser: [1, 400],
  fill: [1, 1],
  picker: [1, 1],
  lasso: [1, 1],
  move: [1, 1],
};

/** Which controls a brush offers. */
export interface BrushControls {
  color: boolean;
  opacity: boolean;
  hardness: boolean;
  flow: boolean;
  /** Pen pressure can drive the size. */
  pressure: boolean;
  /** Pen pressure can drive the darkness (pencil), the opacity (brush, spray) or the tone (halftone). */
  pressureOpacity: boolean;
  grain: boolean;
  density: boolean;
  pitch: boolean;
  aliased: boolean;
}

const NO_CONTROLS: BrushControls = {
  color: false,
  opacity: false,
  hardness: false,
  flow: false,
  pressure: false,
  pressureOpacity: false,
  grain: false,
  density: false,
  pitch: false,
  aliased: false,
};

const controls = (on: Partial<BrushControls>): BrushControls => ({ ...NO_CONTROLS, ...on });

export const BRUSH_CONTROLS: Record<BrushKind, BrushControls> = {
  pencil: controls({ color: true, opacity: true, pressure: true, pressureOpacity: true, grain: true }),
  fineliner: controls({ color: true, opacity: true }),
  brush: controls({ color: true, opacity: true, hardness: true, flow: true, pressure: true, pressureOpacity: true }),
  pixel: controls({ color: true, opacity: true, pressure: true }),
  halftone: controls({ color: true, opacity: true, hardness: true, flow: true, pressure: true, pressureOpacity: true, pitch: true }),
  spray: controls({ color: true, opacity: true, flow: true, pressure: true, pressureOpacity: true, density: true }),
  eraser: controls({ opacity: true, hardness: true, pressure: true, aliased: true }),
  fill: controls({ color: true, opacity: true }),
  picker: controls({}),
  lasso: controls({ aliased: true }),
  move: controls({}),
};

/** Tiles the paint undo backs up (see rasterStore). */
export const TILE = 256;

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function parseHex(color: string): RGB {
  const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export const toHex = (c: RGB): string => '#' + [c.r, c.g, c.b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Grows a rect to include a circle. */
function include(r: Rect | null, x: number, y: number, radius: number): Rect {
  const x0 = Math.floor(x - radius - 1);
  const y0 = Math.floor(y - radius - 1);
  const x1 = Math.ceil(x + radius + 1);
  const y1 = Math.ceil(y + radius + 1);
  if (!r) return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  const nx0 = Math.min(r.x, x0);
  const ny0 = Math.min(r.y, y0);
  const nx1 = Math.max(r.x + r.w, x1);
  const ny1 = Math.max(r.y + r.h, y1);
  return { x: nx0, y: ny0, w: nx1 - nx0, h: ny1 - ny0 };
}

/** Clips a rect to a canvas. Null when nothing is left. */
export function clipRect(r: Rect, w: number, h: number): Rect | null {
  const x0 = Math.max(0, r.x);
  const y0 = Math.max(0, r.y);
  const x1 = Math.min(w, r.x + r.w);
  const y1 = Math.min(h, r.y + r.h);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** The smallest rect holding both (either may be missing). */
export function unionRect(a: Rect | null, b: Rect | null): Rect | null {
  if (!a) return b;
  if (!b) return a;
  const x0 = Math.min(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const x1 = Math.max(a.x + a.w, b.x + b.w);
  const y1 = Math.max(a.y + a.h, b.y + b.h);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

// ---- paper tooth (pencil) ------------------------------------------------------------------

const GRAIN_SIZE = 256;
const GRAIN_LEVELS = 16;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let toothNoise: Float32Array | null = null;

/**
 * The tooth of the paper: tileable noise (pixel grit over slightly larger bumps), the same on
 * every device, equalised so that a threshold `t` keeps the share `1 - t` of the pixels.
 */
function paperTooth(): Float32Array {
  if (toothNoise) return toothNoise;
  const n = GRAIN_SIZE;
  const m = n / 2;
  const rnd = mulberry32(0x70074);
  const fine = new Float32Array(n * n);
  for (let i = 0; i < fine.length; i++) fine[i] = rnd();
  const coarse = new Float32Array(m * m);
  for (let i = 0; i < coarse.length; i++) coarse[i] = rnd();
  const mix = new Float32Array(n * n);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      // the half-resolution bumps, bilinear, wrapping at the tile's edges
      const x0 = (x >> 1) % m;
      const y0 = (y >> 1) % m;
      const x1 = (x0 + 1) % m;
      const y1 = (y0 + 1) % m;
      const tx = (x & 1) / 2;
      const ty = (y & 1) / 2;
      const top = coarse[y0 * m + x0]! * (1 - tx) + coarse[y0 * m + x1]! * tx;
      const bottom = coarse[y1 * m + x0]! * (1 - tx) + coarse[y1 * m + x1]! * tx;
      mix[y * n + x] = (top * (1 - ty) + bottom * ty) * 0.6 + fine[y * n + x]! * 0.4;
    }
  }
  const order = new Uint32Array(n * n);
  for (let i = 0; i < order.length; i++) order[i] = i;
  order.sort((a, b) => mix[a]! - mix[b]!);
  const out = new Float32Array(n * n);
  for (let rank = 0; rank < order.length; rank++) out[order[rank]!] = rank / (order.length - 1);
  toothNoise = out;
  return out;
}

/** Stamps are built here before they go onto the stroke (one small canvas, reused). */
let stampCanvas: HTMLCanvasElement | null = null;
const toothPatterns: (CanvasPattern | null)[] = [];

function stampContext(w: number, h: number): CanvasRenderingContext2D | null {
  if (!stampCanvas) stampCanvas = document.createElement('canvas');
  if (stampCanvas.width < w) stampCanvas.width = w;
  if (stampCanvas.height < h) stampCanvas.height = h;
  const ctx = stampCanvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, w, h);
  return ctx;
}

/** The tooth as an alpha mask: level 0 lets everything through, the top level only the peaks. */
function toothPattern(ctx: CanvasRenderingContext2D, level: number): CanvasPattern | null {
  const cached = toothPatterns[level];
  if (cached) return cached;
  const noise = paperTooth();
  const tile = document.createElement('canvas');
  tile.width = GRAIN_SIZE;
  tile.height = GRAIN_SIZE;
  const tctx = tile.getContext('2d');
  if (!tctx) return null;
  const img = tctx.createImageData(GRAIN_SIZE, GRAIN_SIZE);
  const threshold = (level / (GRAIN_LEVELS - 1)) * 0.92;
  const soft = 0.08;
  for (let i = 0; i < noise.length; i++) {
    const k = clamp01((noise[i]! - (threshold - soft)) / (soft * 2));
    img.data[i * 4 + 3] = Math.round(k * k * (3 - 2 * k) * 255);
  }
  tctx.putImageData(img, 0, 0);
  const pattern = ctx.createPattern(tile, 'repeat');
  toothPatterns[level] = pattern;
  return pattern;
}

// ---- whole pixels (pixel brush, hard eraser) -----------------------------------------------

const discRuns = new Map<number, Int16Array>();

/** Rows of a disc `d` pixels across with no anti-aliasing: [first, last] column per row. */
function pixelDisc(d: number): Int16Array {
  let runs = discRuns.get(d);
  if (runs) return runs;
  runs = new Int16Array(d * 2);
  const r = d / 2;
  // a touch inside the true circle, so 3 px is a plus and 4 px loses its corners
  const rr = (r - 0.1) * (r - 0.1);
  for (let i = 0; i < d; i++) {
    const cy = i + 0.5 - r;
    const span = rr - cy * cy;
    if (span < 0) {
      runs[i * 2] = 1;
      runs[i * 2 + 1] = 0;
      continue;
    }
    const dx = Math.sqrt(span);
    runs[i * 2] = Math.max(0, Math.ceil(r - dx - 0.5));
    runs[i * 2 + 1] = Math.min(d - 1, Math.floor(r + dx - 0.5));
  }
  discRuns.set(d, runs);
  return runs;
}

// ---- halftone screen -----------------------------------------------------------------------

const HALFTONE_LEVELS = 12;

/**
 * Tiles of a 45 degree dot screen, one per tone: the dots of every tone sit in the same places
 * and only grow, so painting a lighter tone over a darker one changes nothing.
 */
function halftoneTiles(rgb: RGB, pitch: number): HTMLCanvasElement[] {
  // a 45 degree screen with `pitch` between dots repeats every pitch * sqrt(2), two dots per tile
  const s = Math.max(4, Math.round(pitch * Math.SQRT2));
  const tiles: HTMLCanvasElement[] = [];
  for (let k = 1; k <= HALFTONE_LEVELS; k++) {
    const tile = document.createElement('canvas');
    tile.width = s;
    tile.height = s;
    const ctx = tile.getContext('2d');
    if (ctx) {
      // at full tone neighbouring dots (s / sqrt(2) apart) just merge
      const r = s * 0.37 * (k / HALFTONE_LEVELS);
      ctx.fillStyle = `rgb(${rgb.r},${rgb.g},${rgb.b})`;
      ctx.beginPath();
      for (const [cx, cy] of [[0, 0], [s, 0], [0, s], [s, s], [s / 2, s / 2]] as const) {
        ctx.moveTo(cx + r, cy);
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
      }
      ctx.fill();
    }
    tiles.push(tile);
  }
  return tiles;
}

// ---- strokes ---------------------------------------------------------------------------------

export interface StrokePoint {
  x: number;
  y: number;
  /** 0..1 */
  pressure: number;
}

/** The pixel selection a stroke must stay inside. */
export interface StrokeClip {
  path: Path2D;
  /** Set when the selection has a hard edge: whole-pixel brushes test every pixel against it. */
  test?: (x: number, y: number) => boolean;
}

/**
 * One stroke in progress. `layer` receives the pixels; `scratch` (same size) holds paint
 * brushes until the end. `backup(rect)` is called before pixels of the layer change, so the
 * caller can copy the tiles it needs for undo.
 */
export class StrokeSession {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly target: CanvasRenderingContext2D;
  private readonly direct: boolean;
  /** Whole pixels, stepped cell by cell, instead of anti-aliased dabs. */
  private readonly aliased: boolean;
  private readonly rgb: RGB;
  private readonly spacing: number;
  private readonly screen: (CanvasPattern | null)[] = [];
  private readonly pixelTest: ((x: number, y: number) => boolean) | null;
  private clipped = false;
  private last: StrokePoint | null = null;
  private smooth: StrokePoint | null = null;
  private cell: { x: number; y: number } | null = null;
  private carry = 0;
  private dirty: Rect | null = null;
  private stamps = 0;

  constructor(
    private readonly layer: HTMLCanvasElement,
    private readonly scratch: HTMLCanvasElement,
    private readonly p: BrushParams,
    private readonly backup: (rect: Rect) => void,
    clip: StrokeClip | null = null,
  ) {
    this.direct = p.kind === 'eraser';
    this.aliased = p.kind === 'pixel' || (p.kind === 'eraser' && p.aliased);
    this.rgb = parseHex(p.color);
    const hard = p.hardness >= 0.99 || !BRUSH_CONTROLS[p.kind].hardness;
    this.spacing = this.aliased
      ? 1
      : p.kind === 'halftone'
        ? Math.max(1, p.size * 0.12)
        : Math.max(0.5, p.size * (hard && p.kind !== 'spray' ? 0.2 : 0.1));
    const lctx = layer.getContext('2d');
    const sctx = scratch.getContext('2d');
    if (!lctx || !sctx) throw new Error('2D canvas unavailable');
    this.ctx = lctx;
    this.target = this.direct ? lctx : sctx;
    if (!this.direct) {
      sctx.setTransform(1, 0, 0, 1, 0, 0);
      sctx.globalCompositeOperation = 'source-over';
      sctx.clearRect(0, 0, scratch.width, scratch.height);
    }
    // a hard-edged selection is tested pixel by pixel by the whole-pixel brushes; every other
    // combination clips to the outline
    this.pixelTest = this.aliased && clip?.test ? clip.test : null;
    if (clip && !this.pixelTest) {
      this.target.save();
      this.target.clip(clip.path);
      this.clipped = true;
    }
    if (p.kind === 'halftone') {
      for (const tile of halftoneTiles(this.rgb, p.pitch)) this.screen.push(this.target.createPattern(tile, 'repeat'));
    }
  }

  get bounds(): Rect | null {
    return this.dirty;
  }

  private effectiveSize(pressure: number): number {
    const p = this.p;
    if (!p.pressureSize || !BRUSH_CONTROLS[p.kind].pressure) return p.size;
    return Math.max(0.5, p.size * (0.15 + 0.85 * clamp01(pressure)));
  }

  /** How much of the brush's strength the pen pressure lets through. */
  private fade(pressure: number): number {
    const p = this.p;
    return p.pressureOpacity && BRUSH_CONTROLS[p.kind].pressureOpacity ? 0.2 + 0.8 * clamp01(pressure) : 1;
  }

  private effectiveAlpha(pressure: number): number {
    const p = this.p;
    // brush and spray build up stamp by stamp, at their flow
    if (p.kind === 'brush' || p.kind === 'spray') return clamp01(p.flow * this.fade(pressure));
    if (this.direct || p.kind === 'pencil') {
      // stamps overlap about size/spacing times along the path; pick a per-stamp alpha whose
      // accumulation over that many stamps equals the wanted strength
      const total = clamp01((this.direct ? p.opacity : 1) * this.fade(pressure));
      const n = Math.max(1, this.effectiveSize(pressure) / this.spacing);
      return total >= 1 ? 1 : 1 - Math.pow(1 - total, 1 / n);
    }
    return 1;
  }

  begin(pt: StrokePoint): void {
    this.last = pt;
    this.smooth = pt;
    if (this.aliased) {
      this.cell = { x: Math.floor(pt.x), y: Math.floor(pt.y) };
      this.stamp(this.cell.x + 0.5, this.cell.y + 0.5, pt.pressure);
      return;
    }
    this.stamp(pt.x, pt.y, pt.pressure);
  }

  extend(raw: StrokePoint): void {
    if (!this.last || !this.smooth) return this.begin(raw);
    // light position smoothing: fewer wobbles from a shaky hand, still tight to the pointer
    const kind = this.p.kind;
    const k = this.aliased ? 0.85 : kind === 'pencil' || kind === 'fineliner' ? 0.7 : 0.55;
    const pt: StrokePoint = {
      x: this.smooth.x + (raw.x - this.smooth.x) * k,
      y: this.smooth.y + (raw.y - this.smooth.y) * k,
      pressure: raw.pressure,
    };
    this.smooth = pt;
    const from = this.last;
    if (this.aliased) return this.extendCells(from, pt);
    const dx = pt.x - from.x;
    const dy = pt.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-3) return;
    let t = this.carry;
    while (t <= len) {
      const f = t / len;
      this.stamp(from.x + dx * f, from.y + dy * f, from.pressure + (pt.pressure - from.pressure) * f);
      t += this.spacing;
    }
    this.carry = t - len;
    this.last = pt;
  }

  /** Whole pixels: walk the cells between the two points (Bresenham), so a 1 px line has no gaps. */
  private extendCells(from: StrokePoint, pt: StrokePoint): void {
    const to = { x: Math.floor(pt.x), y: Math.floor(pt.y) };
    let { x, y } = this.cell ?? to;
    const dx = Math.abs(to.x - x);
    const dy = -Math.abs(to.y - y);
    const sx = x < to.x ? 1 : -1;
    const sy = y < to.y ? 1 : -1;
    const steps = Math.max(1, dx, -dy);
    let err = dx + dy;
    let i = 0;
    while (x !== to.x || y !== to.y) {
      const e2 = err * 2;
      if (e2 >= dy) {
        err += dy;
        x += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y += sy;
      }
      i++;
      this.stamp(x + 0.5, y + 0.5, from.pressure + (pt.pressure - from.pressure) * (i / steps));
    }
    this.cell = to;
    this.last = pt;
  }

  /** The pointer is resting: a spray can keeps spraying where it points. */
  dwell(): void {
    if (this.p.kind === 'spray' && this.last) this.stamp(this.last.x, this.last.y, this.last.pressure);
  }

  private stamp(x: number, y: number, pressure: number): void {
    const size = this.effectiveSize(pressure);
    const r = size / 2;
    if (this.direct) this.backup(include(null, x, y, r));
    this.dirty = include(this.dirty, x, y, r);
    const ctx = this.target;
    ctx.save();
    ctx.globalAlpha = this.effectiveAlpha(pressure);
    ctx.globalCompositeOperation = this.direct ? 'destination-out' : 'source-over';
    if (this.aliased) this.stampPixels(ctx, x, y, size);
    else if (this.p.kind === 'pencil') this.stampPencil(ctx, x, y, r, pressure);
    else if (this.p.kind === 'halftone') this.stampHalftone(ctx, x, y, r, pressure);
    else if (this.p.kind === 'spray') this.stampSpray(ctx, x, y, r, pressure);
    else this.stampRound(ctx, x, y, r);
    ctx.restore();
    this.stamps++;
  }

  private stampRound(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
    const { r: cr, g: cg, b: cb } = this.rgb;
    const hard = this.p.hardness >= 0.99 || !BRUSH_CONTROLS[this.p.kind].hardness;
    if (hard || r < 1) {
      ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    } else {
      const g = ctx.createRadialGradient(x, y, r * this.p.hardness, x, y, r);
      g.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.fillStyle = g;
    }
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.5, r), 0, Math.PI * 2);
    ctx.fill();
  }

  /** A disc of whole pixels centred on the pixel under the pointer. */
  private stampPixels(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
    const d = Math.max(1, Math.round(size));
    const left = Math.round(x - d / 2);
    const top = Math.round(y - d / 2);
    const runs = pixelDisc(d);
    const test = this.pixelTest;
    ctx.fillStyle = `rgb(${this.rgb.r},${this.rgb.g},${this.rgb.b})`;
    for (let i = 0; i < d; i++) {
      const a = runs[i * 2]!;
      const b = runs[i * 2 + 1]!;
      if (b < a) continue;
      if (!test) {
        ctx.fillRect(left + a, top + i, b - a + 1, 1);
        continue;
      }
      // inside a hard-edged selection: only the pixels it holds
      let start = -1;
      for (let j = a; j <= b + 1; j++) {
        const inside = j <= b && test(left + j, top + i);
        if (inside && start < 0) start = j;
        else if (!inside && start >= 0) {
          ctx.fillRect(left + start, top + i, j - start, 1);
          start = -1;
        }
      }
    }
  }

  /**
   * Graphite: a slightly soft dab seen through the paper's tooth. A light touch only catches
   * the peaks of the tooth; pressing harder reaches into the valleys and the line fills in.
   */
  private stampPencil(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, pressure: number): void {
    const t = clamp01(this.p.grain) * (1 - 0.6 * clamp01(pressure));
    const level = Math.round(t * (GRAIN_LEVELS - 1));
    const rr = Math.max(0.5, r);
    const ox = Math.floor(x - rr) - 1;
    const oy = Math.floor(y - rr) - 1;
    const w = Math.ceil(rr * 2) + 3;
    const sctx = stampContext(w, w);
    if (!sctx || !stampCanvas) return;
    const { r: cr, g: cg, b: cb } = this.rgb;
    if (rr < 1.5) {
      sctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    } else {
      const g = sctx.createRadialGradient(x - ox, y - oy, rr * 0.6, x - ox, y - oy, rr);
      g.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      sctx.fillStyle = g;
    }
    sctx.beginPath();
    sctx.arc(x - ox, y - oy, rr, 0, Math.PI * 2);
    sctx.fill();
    const tooth = level > 0 ? toothPattern(sctx, level) : null;
    if (tooth) {
      // the pattern starts at the layer's origin wherever the stamp is: the tooth belongs to the paper
      sctx.globalCompositeOperation = 'destination-in';
      sctx.fillStyle = tooth;
      sctx.setTransform(1, 0, 0, 1, -ox, -oy);
      sctx.fillRect(ox, oy, w, w);
      sctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    ctx.drawImage(stampCanvas, 0, 0, w, w, ox, oy, w, w);
  }

  /**
   * Screentone: the dab's strength picks how large the dots of the screen are, from its full
   * tone in the core down to pinpoints at the soft rim. Each tone is a disc filled with that
   * tone's dot pattern, lightest and widest first.
   */
  private stampHalftone(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, pressure: number): void {
    const tone = clamp01(this.p.flow * this.fade(pressure));
    const top = Math.max(1, Math.round(tone * HALFTONE_LEVELS));
    const h = clamp01(this.p.hardness);
    let drawn = -1;
    for (let k = h >= 0.99 ? top : 1; k <= top; k++) {
      const pattern = this.screen[k - 1];
      if (!pattern) continue;
      const radius = Math.max(0.5, r * (h + (1 - h) * (1 - (k - 1) / top)));
      if (radius === drawn) continue;
      drawn = radius;
      ctx.fillStyle = pattern;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /** A puff of specks, thicker towards the middle of the can's cone. */
  private stampSpray(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, pressure: number): void {
    const size = r * 2;
    const speck = Math.max(0.5, Math.min(2.5, size * 0.012));
    const count = Math.max(1, Math.round(clamp01(this.p.density) * (10 + size * 0.3) * (0.4 + 0.6 * clamp01(pressure))));
    ctx.fillStyle = `rgb(${this.rgb.r},${this.rgb.g},${this.rgb.b})`;
    ctx.beginPath();
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = r * Math.pow(Math.random(), 0.7);
      const sr = speck * (0.5 + Math.random());
      const sx = x + Math.cos(a) * d;
      const sy = y + Math.sin(a) * d;
      ctx.moveTo(sx + sr, sy);
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    }
    ctx.fill();
  }

  private unclip(): void {
    if (!this.clipped) return;
    this.clipped = false;
    this.target.restore();
  }

  /** Composites a paint stroke onto the layer. Returns the changed rect (layer pixels), or null. */
  end(): Rect | null {
    this.unclip();
    const rect = this.dirty ? clipRect(this.dirty, this.layer.width, this.layer.height) : null;
    if (!rect || !this.stamps) return null;
    if (!this.direct) {
      this.backup(rect);
      const ctx = this.ctx;
      ctx.save();
      ctx.globalAlpha = clamp01(this.p.opacity);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(this.scratch, rect.x, rect.y, rect.w, rect.h, rect.x, rect.y, rect.w, rect.h);
      ctx.restore();
      this.scratch.getContext('2d')?.clearRect(rect.x, rect.y, rect.w, rect.h);
    }
    return rect;
  }

  /** Drops a paint stroke in progress (the caller restores the layer for a direct one). */
  cancel(): void {
    this.unclip();
    if (this.direct) return;
    const rect = this.dirty ? clipRect(this.dirty, this.scratch.width, this.scratch.height) : null;
    if (rect) this.scratch.getContext('2d')?.clearRect(rect.x, rect.y, rect.w, rect.h);
  }
}

// ---- fill and eyedropper ----------------------------------------------------------------

/** The part of the layer a fill may touch: a pixel selection's mask (RGBA) and where it sits. */
export interface FillRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  data: Uint8ClampedArray;
}

/**
 * Flood fill from a pixel over the layer: contiguous pixels within `tolerance` (0..255, on the
 * largest channel difference, alpha included) take the colour at `opacity`. The anti-aliased
 * rim the fill stops at (a stroke's soft edge) gets the colour composited underneath it, so no
 * light seam is left along the outline. With a `region` the fill never leaves it. Returns the
 * changed rect, or null when the seed is out of bounds (or outside the region).
 */
export function floodFill(
  canvas: HTMLCanvasElement,
  sx: number,
  sy: number,
  color: string,
  opacity: number,
  tolerance: number,
  backup: (rect: Rect) => void,
  region: FillRegion | null = null,
): Rect | null {
  const w = canvas.width;
  const h = canvas.height;
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  if (x0 < 0 || y0 < 0 || x0 >= w || y0 >= h) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const seed = (y0 * w + x0) * 4;
  const sr = d[seed]!;
  const sg = d[seed + 1]!;
  const sb = d[seed + 2]!;
  const sa = d[seed + 3]!;
  const tol = Math.max(0, Math.min(255, tolerance));
  // 0 untouched, 1 filled, 2 rim (a non-matching neighbour of a filled pixel), 3 outside the region
  const mark = new Uint8Array(w * h);
  if (region) {
    mark.fill(3);
    const rx1 = Math.min(w, region.x + region.w);
    const ry1 = Math.min(h, region.y + region.h);
    for (let y = Math.max(0, region.y); y < ry1; y++) {
      for (let x = Math.max(0, region.x); x < rx1; x++) {
        if (region.data[((y - region.y) * region.w + (x - region.x)) * 4 + 3]! >= 128) mark[y * w + x] = 0;
      }
    }
    if (mark[y0 * w + x0] === 3) return null;
  }
  /** May the fill spread into pixel `q`? */
  const open = (q: number) => {
    if (mark[q] === 1 || mark[q] === 3) return false;
    const i = q * 4;
    // a fully transparent pixel matches any other fully transparent pixel, whatever its colour
    if (sa === 0 && d[i + 3] === 0) return true;
    return (
      Math.abs(d[i]! - sr) <= tol && Math.abs(d[i + 1]! - sg) <= tol && Math.abs(d[i + 2]! - sb) <= tol && Math.abs(d[i + 3]! - sa) <= tol
    );
  };
  const stack: number[] = [y0 * w + x0];
  const { r, g, b } = parseHex(color);
  const a = clamp01(opacity);
  let minX = x0;
  let minY = y0;
  let maxX = x0;
  let maxY = y0;
  const rim = (q: number) => {
    if (mark[q] === 0) mark[q] = 2;
  };
  // scanline fill: walk left/right along each row, seed the rows above and below
  while (stack.length) {
    const p = stack.pop()!;
    if (mark[p] === 1) continue;
    if (!open(p)) {
      rim(p);
      continue;
    }
    const y = Math.floor(p / w);
    const x = p - y * w;
    let xl = x;
    while (xl > 0 && open(p - (x - xl) - 1)) xl--;
    let xr = x;
    while (xr < w - 1 && open(p + (xr - x) + 1)) xr++;
    if (xl > 0) rim(y * w + xl - 1);
    if (xr < w - 1) rim(y * w + xr + 1);
    for (let cx = xl; cx <= xr; cx++) {
      const q = y * w + cx;
      mark[q] = 1;
      if (y > 0 && mark[q - w] !== 1) stack.push(q - w);
      if (y < h - 1 && mark[q + w] !== 1) stack.push(q + w);
    }
    if (xl < minX) minX = xl;
    if (xr > maxX) maxX = xr;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  // the rim extends the changed rect by a pixel
  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(w - 1, maxX + 1);
  maxY = Math.min(h - 1, maxY + 1);
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const q = y * w + x;
      const m = mark[q];
      if (m !== 1 && m !== 2) continue;
      const i = q * 4;
      const da = d[i + 3]! / 255;
      if (m === 1) {
        // source-over: the fill colour at `a` over the pixel
        const oa = a + da * (1 - a);
        if (oa > 0) {
          d[i] = Math.round((r * a + d[i]! * da * (1 - a)) / oa);
          d[i + 1] = Math.round((g * a + d[i + 1]! * da * (1 - a)) / oa);
          d[i + 2] = Math.round((b * a + d[i + 2]! * da * (1 - a)) / oa);
        }
        d[i + 3] = Math.round(oa * 255);
      } else {
        // destination-over: the fill colour at `a` underneath the rim pixel
        const oa = da + a * (1 - da);
        if (oa > 0) {
          d[i] = Math.round((d[i]! * da + r * a * (1 - da)) / oa);
          d[i + 1] = Math.round((d[i + 1]! * da + g * a * (1 - da)) / oa);
          d[i + 2] = Math.round((d[i + 2]! * da + b * a * (1 - da)) / oa);
        }
        d[i + 3] = Math.round(oa * 255);
      }
    }
  }
  const rect = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  backup(rect);
  ctx.putImageData(img, 0, 0, rect.x, rect.y, rect.w, rect.h);
  return rect;
}

/** Colour of a pixel of the layer, or null when it is (nearly) transparent or out of bounds. */
export function pickColor(canvas: HTMLCanvasElement, x: number, y: number): string | null {
  const px = Math.floor(x);
  const py = Math.floor(y);
  if (px < 0 || py < 0 || px >= canvas.width || py >= canvas.height) return null;
  const d = canvas.getContext('2d')?.getImageData(px, py, 1, 1).data;
  if (!d || d[3]! < 8) return null;
  return toHex({ r: d[0]!, g: d[1]!, b: d[2]! });
}
